// «Mensajes programados» — what this conversation has waiting, and the three moves a person can
// make about it: send it now, move it, call it off (port of ScheduledMessagesSheet.swift), plus
// the quiet «N mensajes programados · Ver» strip above the composer (ScheduledMessagesStrip — accent
// on the raised surface, not the undelivered strip's warning: nothing has gone wrong).
import { useContext, useEffect, useState } from 'react';
import { data } from '../../../bridge/client';
import * as ScheduledPostPresentation from '../../../core/scheduledPostPresentation';
import { scheduledWhenLabel } from '../../../core/scheduledPost';
import type { ScheduledPostRecord } from '../../../data/db/daos';
import { t, tf } from '../../../i18n/i18n';
import { useLive, useMinuteClock } from '../../hooks';
import { Dialog } from '../../kit/Kit';
import { ShellContext } from '../../shell/ShellContext';
import { ScheduleSheet } from './ScheduleSheet';
import './scheduled.css';

/** The conversation's (or the thread's) scheduled rows from the store; refreshed from the server on open. */
export function useScheduled(channelId: string, rootId: string | null): ScheduledPostRecord[] {
  const shell = useContext(ShellContext);
  const teamId = shell?.team.id ?? '';
  useEffect(() => {
    if (teamId) void data.call('scheduledRefresh', teamId).catch(() => undefined);
  }, [teamId, channelId]);
  const live = useLive(() => data.call('scheduledForChannel', channelId), ['scheduledPosts'], [channelId]);
  const rows = live.value ?? [];
  // The thread pane shows its own thread's; the conversation shows every one of the channel's.
  return rootId ? rows.filter((r) => r.rootId === rootId) : rows;
}

function headline(row: ScheduledPostRecord, now: number): string {
  // The server tried and could not — that one is never arriving, and saying so is the only honest row.
  if (row.errorCode) return t('schedule.row.failed');
  // Its moment has passed but the server has not reported back yet: in flight, not stuck.
  if (row.scheduledAt <= now) return t('schedule.row.sending');
  return scheduledWhenLabel(row.scheduledAt, now);
}

export function ScheduledStrip({ count, onOpen }: { count: number; onOpen(): void }) {
  if (count <= 0) return null;
  return (
    <button type="button" className="sched-strip" onClick={onOpen}>
      <span>{count === 1 ? t('schedule.strip.one') : tf('schedule.strip.many', count)}</span>
      <span className="spacer" />
      <span className="sched-strip-action">{t('schedule.strip.action')}</span>
    </button>
  );
}

export function ScheduledList({ rows, onClose }: { rows: ScheduledPostRecord[]; onClose(): void }) {
  const now = useMinuteClock();
  const shell = useContext(ShellContext);
  const [rescheduling, setRescheduling] = useState<string | null>(null);
  const fail = () => shell?.toast(t('web.scheduled.actionFailed'), 'error');
  if (rescheduling) {
    return (
      <ScheduleSheet
        // Dismissing the sheet goes back to the list; a picked time closes both (the Mac's flow).
        onClose={() => setRescheduling((id) => (id === rescheduling ? null : id))}
        onSchedule={(at) => {
          const id = rescheduling;
          setRescheduling('');
          onClose();
          void data.call('scheduledReschedule', id, at).then((ok) => ok || fail(), fail);
        }}
      />
    );
  }
  return (
    <Dialog title={t('schedule.list.title')} onClose={onClose} width={480}>
      {rows.length === 0 ? (
        <p className="sched-empty">{t('schedule.list.empty')}</p>
      ) : (
        <div className="sched-rows">
          {rows.map((row) => {
            const files = ScheduledPostPresentation.fileIds(row.rawJson).length;
            return (
              <div key={row.id} className="sched-row" data-scheduled-id={row.id}>
                <div className={`sched-row-when${row.errorCode ? ' failed' : ''}`}>{headline(row, now)}</div>
                {row.message ? <div className="sched-row-text">{row.message}</div> : null}
                {files > 0 ? <div className="sched-row-files">{files === 1 ? t('schedule.attachment') : tf('schedule.attachments', files)}</div> : null}
                <div className="sched-row-actions">
                  <button
                    type="button"
                    className="link-button accent"
                    onClick={() => {
                      onClose();
                      void data.call('scheduledSendNow', row.id).then((ok) => ok || fail(), fail);
                    }}
                  >
                    {t('schedule.row.sendNow')}
                  </button>
                  <button type="button" className="link-button" onClick={() => setRescheduling(row.id)}>
                    {t('schedule.row.reschedule')}
                  </button>
                  <button type="button" className="link-button danger" onClick={() => void data.call('scheduledCancel', row.id).then((ok) => ok || fail(), fail)}>
                    {t('schedule.row.cancel')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
